import { useRef, useState } from 'react'

import Timestamp from './Timestamp'
import { errorStatus, useSummaryFeedbackAudioUrl } from './useSummaryFeedback'

import styles from './SummaryFeedbackPage.module.scss'

import type { SummaryFeedbackDetail } from '@/api/meetingSummaryFeedback'
import Button from '@/components/Button'
import { useToast } from '@/components/Toast/ToastContext'

const audioErrorMessages: Record<number, string> = {
  403: 'You need internal admin access to play this audio.',
  404: 'No audio is stored for this rating.',
  409: 'Audio is withheld for this rating.',
}

const AudioSection = ({ detail }: { detail: SummaryFeedbackDetail }) => {
  const { updateToast } = useToast()
  const [expired, setExpired] = useState(detail.audioStatus === 'expired')
  const [playbackFailed, setPlaybackFailed] = useState(false)
  const refreshedAfterError = useRef(false)
  const presign = useSummaryFeedbackAudioUrl(detail.id, {
    onSuccess: () => setPlaybackFailed(false),
    onError: (error) => {
      const status = errorStatus(error)
      if (status === 410) {
        setExpired(true)
        return
      }
      updateToast(
        (status !== undefined && audioErrorMessages[status]) || "Couldn't load the audio. Try again.",
        'error'
      )
    },
  })

  const load = () => {
    refreshedAfterError.current = false
    setPlaybackFailed(false)
    presign.mutate()
  }

  // A presigned URL lapses after 15 minutes, so a media error earns one fresh URL before we give up.
  const handleMediaError = () => {
    if (refreshedAfterError.current) {
      setPlaybackFailed(true)
      return
    }
    refreshedAfterError.current = true
    presign.mutate()
  }

  let body
  if (detail.audioStatus === 'withheld') {
    body = (
      <p className={styles.notice}>
        {detail.meetingDeleted
          ? 'The meeting was deleted, so its audio is withheld.'
          : 'Withheld: the user had no disclosure or data-sharing consent.'}
      </p>
    )
  } else if (detail.audioStatus === 'absent') {
    body = <p className={styles.notice}>No audio was stored for this rating.</p>
  } else if (expired) {
    body = (
      <div className={styles.audioControls}>
        <p className={styles.notice}>
          Audio expired
          {detail.audioExpiresAt && (
            <>
              {' '}
              <Timestamp value={detail.audioExpiresAt} />
            </>
          )}
        </p>
        <Button title="Load audio" type="light" isDisabled onClick={load} />
      </div>
    )
  } else if (presign.data && !playbackFailed) {
    body = (
      <audio
        className={styles.audio}
        controls
        preload="metadata"
        src={presign.data.url}
        onLoadedMetadata={() => {
          refreshedAfterError.current = false
        }}
        onError={handleMediaError}
      />
    )
  } else {
    body = (
      <div className={styles.audioControls}>
        {playbackFailed && (
          <p className={styles.notice} role="alert">
            The audio couldn&apos;t be played.
          </p>
        )}
        <Button title="Load audio" type="light" isLoading={presign.isPending} onClick={load} />
      </div>
    )
  }

  return (
    <section className={styles.section} aria-labelledby="summary-feedback-audio" aria-busy={presign.isPending}>
      <h2 id="summary-feedback-audio">Audio</h2>
      {body}
    </section>
  )
}

export default AudioSection
